import { useEffect, type ReactNode } from 'react';
import type { FigClient } from '../core/api/client.js';
import { trackEvent } from '../lib/telemetry.js';

/**
 * Time in the portal (2026-10-21), the same way the app measures time in the app: an opaque key
 * per visit, a heartbeat every three minutes while the tab is in front of somebody, and screen
 * views by NAME. No identity is sent - the API takes who from the token it already validated.
 *
 * Rasel: usage reports at user, client-company and agency level, "including how long". Until
 * this, every visit to the portal was invisible.
 */

const HEARTBEAT_MS = 3 * 60 * 1000;
/** The API's own idle window. A tab hidden for longer than this comes back as a new visit. */
const NEW_VISIT_AFTER_MS = 10 * 60 * 1000;

function newKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

let currentKey = newKey();
let hiddenAt: number | null = null;

/** The visit in progress, so telemetry can tag its envelopes with the same session. */
export const usageKey = () => currentKey;

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? undefined;

function ping(api: FigClient, event?: 'open' | 'foreground' | 'screen', screen?: string) {
  // Never allowed to break a page: measuring use is the least important thing on any screen.
  try {
    api.recordUsage({ sessionKey: currentKey, event, screen, platform: 'web', appVersion: APP_VERSION });
    // Into App Insights as well, so the ops dashboard's "sessions by day" counts the portal too.
    if (event) trackEvent(`usage.${event}`, screen ? { screen } : {});
  } catch { /* deliberately silent */ }
}

/** Mount once, for a signed-in person. `api` null while signed out: there is no tenant to report against. */
export function useUsageReporting(api: FigClient | null, onNewVisit?: (key: string) => void): void {
  useEffect(() => {
    if (!api) return;
    ping(api, 'open');

    const timer = setInterval(() => {
      // Only while the tab is visible: a tab left open behind others all afternoon is not use.
      if (document.visibilityState === 'visible') ping(api);
    }, HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt !== null && Date.now() - hiddenAt > NEW_VISIT_AFTER_MS) {
        currentKey = newKey();
        onNewVisit?.(currentKey);
        ping(api, 'foreground');
      } else {
        ping(api);
      }
      hiddenAt = null;
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [api, onNewVisit]);
}

/**
 * One screen view, by name. A wrapper on the route rather than a hook in every page, so the list
 * of what is counted is in two route files and can be read at a glance. Names must be on the
 * API's allow-list (UsageController.KnownScreens) or they are stored as "other".
 */
export function ScreenView({ api, name, children }: { api: FigClient; name: string; children: ReactNode }) {
  useEffect(() => { ping(api, 'screen', name); }, [api, name]);
  return children;
}
